import { useEffect, useMemo } from 'react';
import tr from 'locale';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { getLicenseType, getProductName } from '../../../../../helpers';
import { planConfigurationToTextValue } from '../../../../../helpers/planConfigurationToTextValue';
import { fetchFaceMachinePlansAction } from '../../../../../store/plans/actions';
import { getLicenseInfoList } from '../../../../../helpers/getLicenseInfoList';
import { useFetchLicenseInfo } from './hooks/useFetchLicenseInfo';
import { useMetadata } from '../../../../../helpers/useMetadata';
import { handleOpenModal } from '../../../../../store/modals/actions';
import FaceMachineResuming from '../../../../modals/FaceMachineResuming';
import SubscriptionActivateModal from '../../../../modals/SubscriptionActivateModal';
import FaceMachinePausing from '../../../../modals/FaceMachinePausing';

export const useFaceMachineInfo = () => {
  // Routes
  const id = useParams().id;
  // Actions
  const dispatch = useDispatch();
  const openActivateSubscription = () =>
    dispatch(
      handleOpenModal({
        component: SubscriptionActivateModal,
        name: 'activate',
        license,
        platform,
      })
    );
  const openResumeFaceMachine = () =>
    dispatch(
      handleOpenModal({
        component: FaceMachineResuming,
        license,
        name: 'fm_license_resuming',
      })
    );
  const openPauseFaceMachine = () =>
    dispatch(
      handleOpenModal({
        component: FaceMachinePausing,
        license,
        name: 'fm_license_pausing',
      })
    );
  // Selectors
  const license = useSelector(state => state.licenseInfo.licenseInfo);
  const licenseLoaded = useSelector(state => state.licenseInfo.licenseLoaded);
  const plans = useSelector(state => state.plans.plans);
  const planFieldsByTypesMap = useSelector(state => state.plans.filedsTypes);
  const planConfiguration = useMemo(() => {
    if (!license) return [];
    if (!plans[license.config.plan_id]) return [];
    return planConfigurationToTextValue({
      plan: plans[license.config.plan_id],
      planFieldsByTypesMap,
    });
  }, [plans, license]);

  const isHolded = license && license.license_status === 'holded';
  const platform = 'face-machine';
  const backUrl = '/app/face-machine/dashboard/';
  const licenseName = tr('face-machine-name');

  let licenseManage, message;

  if (license)
    if (license.isTrial) {
      licenseManage = null;
      message = null;
    } else {
      if (license.subscription_status === 'cancelled') {
        licenseManage = null;
        message = {
          text: tr('license_expired'),
          button: { text: tr('renew_subscription'), onClick: openActivateSubscription },
        };
      } else {
        if (license.status === 1) {
          // "1" means what workspace stopped now
          licenseManage = {
            button: {
              text: tr('button_resume_license', { licenseName }),
              onClick: openResumeFaceMachine,
            },
          };
          message = {
            text: tr('license_stopped', { licenseName }),
            button: {
              text: tr('button_resume_license', { licenseName }),
              onClick: openResumeFaceMachine,
            },
          };
        } else if (license.license_status === 'holded') {
          licenseManage = null;
          message = null;
        } else {
          licenseManage = {
            button: {
              text: tr('button_pause_license', { licenseName }),
              onClick: openPauseFaceMachine,
            },
            text: tr('license_pause_description'),
          };
          message = null;
        }
      }
    }

  const metadata = useMetadata(license, { platform });
  const licenseInfo = getLicenseInfoList(
    license,
    { text: tr('product_name'), value: tr(getProductName(license)) },
    { text: tr('license_type'), value: getLicenseType(license) }
  );

  useEffect(() => {
    if (Object.keys(plans).length === 0) dispatch(fetchFaceMachinePlansAction());
  }, []);

  useFetchLicenseInfo({ id });

  return {
    license,
    platform,
    loaded: licenseLoaded,
    isHolded,
    licenseConfiguration: {
      items: planConfiguration.length > 0 ? planConfiguration : licenseConfiguration,
    },
    metadata,
    licenseInfo,
    backUrl,
    urlId: id,
    message,
    licenseManage,
  };
};

const licenseConfiguration = [
  { text: tr('License Key Type'), value: tr('online') },
  { text: tr('tps'), value: 1 },
  { text: tr('face_storage'), value: 10000 },
  { text: tr('channels'), value: tr('unlimited') },
  { text: tr('calling_individual_processing_functions'), value: tr('unlimited') },
  { text: tr('streaming_video_processing'), value: tr('unlimited') },
  { text: tr('archived_video_processing'), value: tr('unlimited') },
];
